import React, { useState, useEffect, useCallback } from 'react';
import SearchInput from './SearchInput';
import SearchFilters from './SearchFilters';
import SearchResults from './SearchResults';
import SearchSuggestions from './SearchSuggestions';

// Lightweight debounce implementation to replace lodash
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

const SearchPage = () => {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('all'); // 'all', 'doctors', 'healthcare-providers'
  const [filters, setFilters] = useState({
    location: '',
    specialty: '',
    rating: 0,
    availability: '',
    services: []
  });
  
  // Results state
  const [results, setResults] = useState({
    doctors: [],
    healthcareProviders: [],
    total: 0
  });
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState(null);

  // Smart search with autofill
  const performSearch = async (query, type = searchType, currentFilters = filters) => {
    if (!query.trim() && type === 'all') return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const searchParams = new URLSearchParams({
        q: query,
        type: type,
        ...currentFilters,
        ...(currentFilters.location && { location: currentFilters.location }),
        ...(currentFilters.specialty && { specialty: currentFilters.specialty }),
        ...(currentFilters.rating && { rating: currentFilters.rating }),
        ...(currentFilters.availability && { availability: currentFilters.availability })
      });

      // Smart API endpoint selection based on search type
      let apiEndpoint;
      if (type === 'doctors') {
        apiEndpoint = `/api/search/doctors?${searchParams}`;
      } else if (type === 'healthcare-providers') {
        apiEndpoint = `/api/search/providers?${searchParams}`;
      } else {
        apiEndpoint = `/api/search?${searchParams}`;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${apiEndpoint}`);
      
      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Process and categorize results
      const processedResults = {
        doctors: data.doctors || [],
        healthcareProviders: [...(data.providers || []), ...(data.clinics || [])], // Merge providers and clinics
        total: (data.doctors?.length || 0) + (data.providers?.length || 0) + (data.clinics?.length || 0)
      };
      
      setResults(processedResults);
      
    } catch (err) {
      console.error('Search error:', err);
      setError('Unable to perform search. Please try again.');
      setResults({ doctors: [], healthcareProviders: [], total: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  // Get search suggestions for autofill
  const getSuggestions = async (query) => {
    if (!query.trim() || query.length < 2) {
      setSuggestions([]);
      return;
    }
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/search/suggestions?q=${encodeURIComponent(query)}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (err) {
      console.error('Suggestions error:', err);
    }
  };

  // Debounced search and suggestions
  const debouncedSearch = useCallback(
    debounce((query, type, filters) => {
      performSearch(query, type, filters);
    }, 500),
    []
  );

  const debouncedSuggestions = useCallback(
    debounce((query) => {
      getSuggestions(query);
    }, 300),
    []
  );

  // Handle search input changes
  const handleSearchChange = (query) => {
    setSearchQuery(query);
    
    if (query.trim()) {
      debouncedSuggestions(query);
      debouncedSearch(query, searchType, filters);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setResults({ doctors: [], healthcareProviders: [], total: 0 });
    }
  };

  // Handle search type change
  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    if (searchQuery.trim()) {
      debouncedSearch(searchQuery, type, filters);
    }
  };

  // Handle filters change
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    if (searchQuery.trim()) {
      debouncedSearch(searchQuery, searchType, newFilters);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    setSearchQuery(suggestion.text);
    setShowSuggestions(false);
    performSearch(suggestion.text, suggestion.type || searchType, filters);
  };

  // Load initial results on page load
  useEffect(() => {
    // Load featured/popular results by default
    performSearch('', 'all', filters);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-center mb-2">
            Smart Healthcare Search
          </h1>
          <p className="text-center text-sm sm:text-base text-blue-100 mb-6">
            Find doctors, specialists, and healthcare providers with intelligent search
          </p>

          {/* Search Input */}
          <div className="relative max-w-4xl mx-auto">
            <SearchInput
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => setShowSuggestions(suggestions.length > 0)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Search for doctors, specialties, conditions, or healthcare providers..."
              isLoading={isLoading}
            />
            
            {/* Search Suggestions */}
            {showSuggestions && suggestions.length > 0 && (
              <SearchSuggestions
                suggestions={suggestions}
                onSelect={handleSuggestionSelect}
                query={searchQuery}
              />
            )}
          </div>

          {/* Search Type Tabs */}
          <div className="mt-6 -mx-4 px-4 overflow-x-auto pretty-scroll">
            <div className="flex justify-start sm:justify-center min-w-max">
              <div className="bg-white/10 rounded-lg p-1 backdrop-blur-sm flex">
                {[
                  { key: 'all', label: 'All Results', icon: '🔍' },
                  { key: 'doctors', label: 'Doctors', icon: '👨‍⚕️' },
                  { key: 'healthcare-providers', label: 'Healthcare Providers', icon: '🏥' }
                ].map((type) => (
                  <button
                    key={type.key}
                    onClick={() => handleSearchTypeChange(type.key)}
                    className={`whitespace-nowrap px-4 sm:px-6 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
                      searchType === type.key
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <span className="mr-2">{type.icon}</span>
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters Sidebar */}
          <div className="w-full lg:w-80 lg:flex-shrink-0">
            <SearchFilters
              filters={filters}
              onChange={handleFiltersChange}
              searchType={searchType}
            />
          </div>

          {/* Results */}
          <div className="flex-1 min-w-0">
            {error ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <span className="text-red-500 mr-2">⚠️</span>
                  {error}
                </div>
              </div>
            ) : (
              <SearchResults
                results={results}
                searchType={searchType}
                searchQuery={searchQuery}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;